// src/pages/admin/AdminLabBundlePage.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, Button, Chip,
  LinearProgress, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody,
  Snackbar, Divider, CircularProgress,
} from '@mui/material';
import ArchiveIcon      from '@mui/icons-material/Archive';
import UploadFileIcon   from '@mui/icons-material/UploadFile';
import DownloadIcon     from '@mui/icons-material/Download';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { exportBundle, importBundle, getBundleLevels } from '../../api/adminBundleApi';

export default function AdminLabBundlePage() {
  const { t }          = useTranslation('admin');
  const fileInputRef   = useRef(null);
  const [levels,       setLevels]       = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [importing,    setImporting]    = useState(false);
  const [exportLevel,  setExportLevel]  = useState('gold');
  const [importResult, setImportResult] = useState(null);
  const [error,        setError]        = useState('');
  const [snack,        setSnack]        = useState('');

  useEffect(() => {
    getBundleLevels()
      .then(r => setLevels(r.data))
      .catch(() => setLevels({ bronze: {}, silver: {}, gold: {} }));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const r = await exportBundle(exportLevel, { include_topos: true, include_config: true });
      const url  = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nexthoplab-bundle-' + exportLevel + '-' + new Date().toISOString().slice(0,10) + '.zip';
      link.click();
      URL.revokeObjectURL(url);
      setSnack('Export ' + exportLevel + ' telecharge');
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur export');
    } finally { setExporting(false); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      // dry_run d abord
      const dry  = await importBundle(file, true);
      setImportResult({ ...dry.data, dry_run: true, file });
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur import');
    } finally { setImporting(false); }
  };

  const handleConfirmImport = async () => {
    if (!importResult?.file) return;
    setImporting(true);
    try {
      const r = await importBundle(importResult.file, false);
      setImportResult({ ...r.data, dry_run: false });
      setSnack('Import termine : ' + (r.data.labs_imported || 0) + ' labs importes');
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur import');
    } finally { setImporting(false); }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <ArchiveIcon sx={{ fontSize: 28, color: '#b45309' }} />
        <Typography variant="h5" fontWeight={700}>{t('labBundles')}</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>

        {/* Export */}
        <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
          <Typography fontSize={14} fontWeight={600} mb={2}>Export ZIP</Typography>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Niveau</InputLabel>
            <Select value={exportLevel} onChange={e => setExportLevel(e.target.value)} label="Niveau">
              {['bronze','silver','gold'].map(l => (
                <MenuItem key={l} value={l}>
                  {l.charAt(0).toUpperCase() + l.slice(1)}
                  {levels?.[l] && (
                    <Typography component="span" fontSize={11} color="text.secondary" ml={1}>
                      ({levels[l].max_simultaneous || '-'} simultanes max)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button fullWidth variant="contained" disableElevation
            startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport} disabled={exporting}
            sx={{ borderRadius: 2, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
            Exporter niveau {exportLevel}
          </Button>
        </Paper>

        {/* Import */}
        <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
          <Typography fontSize={14} fontWeight={600} mb={2}>Import ZIP</Typography>
          <input type="file" accept=".zip" ref={fileInputRef}
            style={{ display: 'none' }} onChange={handleImportFile} />
          <Button fullWidth variant="outlined"
            startIcon={importing ? <CircularProgress size={14} /> : <UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()} disabled={importing}
            sx={{ borderRadius: 2, mb: 2 }}>
            Selectionner un ZIP
          </Button>

          {/* Dry run result */}
          {importResult?.dry_run && (
            <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                <Typography fontSize={13} fontWeight={600} color="#16a34a">
                  Simulation OK — {importResult.labs_found || 0} labs trouves
                </Typography>
              </Stack>
              <Typography fontSize={12} color="text.secondary" mb={1.5}>
                {importResult.warnings?.join(' | ') || 'Aucun avertissement'}
              </Typography>
              <Button fullWidth variant="contained" disableElevation onClick={handleConfirmImport}
                sx={{ borderRadius: 2, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                Confirmer l import
              </Button>
            </Box>
          )}

          {importResult && !importResult.dry_run && (
            <Alert severity="success">
              Import termine : {importResult.labs_imported || 0} labs importes,
              {' '}{importResult.labs_skipped || 0} ignores.
            </Alert>
          )}
        </Paper>
      </Stack>

      {/* Niveaux */}
      {levels && (
        <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography fontSize={13} fontWeight={600}>Configuration des niveaux</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Niveau</TableCell><TableCell>Simult. max</TableCell>
                <TableCell>Min/mois</TableCell><TableCell>Features</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(levels).map(([name, cfg]) => (
                <TableRow key={name} hover>
                  <TableCell><Chip label={name} size="small"
                    sx={{ fontWeight: 600, bgcolor: name==='gold' ? '#FAC775' : name==='silver' ? '#D3D1C7' : '#F5C4B3',
                      color: name==='gold' ? '#633806' : name==='silver' ? '#444441' : '#712B13' }} /></TableCell>
                  <TableCell>{cfg.max_simultaneous || '-'}</TableCell>
                  <TableCell>{cfg.max_minutes_month || '-'} min</TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {(cfg.features || []).join(', ') || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
